import { MOCK_POWERS, withoutAcePowers } from '@/lib/powers-data';

export type PowerMentionIndexEntry = {
  slug: string;
  label: string;
  pattern: string;
};

let cachedIndex: PowerMentionIndexEntry[] | null = null;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Índice de nomes/slugs para deteção em texto — apenas matching, não dados do preview. */
export function getPowerMentionIndex(): PowerMentionIndexEntry[] {
  if (cachedIndex) return cachedIndex;

  const entries: PowerMentionIndexEntry[] = [];
  const seen = new Set<string>();

  for (const power of withoutAcePowers(MOCK_POWERS)) {
    const patterns = [power.name, power.slug];
    for (const pattern of patterns) {
      const trimmed = pattern.trim();
      if (!trimmed) continue;
      const key = `${power.slug}::${trimmed.toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      entries.push({ slug: power.slug, label: trimmed, pattern: trimmed });
    }
  }

  entries.sort((a, b) => b.pattern.length - a.pattern.length);
  cachedIndex = entries;
  return entries;
}

export function buildPowerMentionPattern(): RegExp {
  const index = getPowerMentionIndex();
  if (index.length === 0) return /$^/;

  const parts = index.map((e) => escapeRegExp(e.pattern));
  // Só corresponde a menções reais (ex.: «Nameglow»), nunca substrings («me» em «Namecolor»).
  return new RegExp(`(?<!\\w)(?:${parts.join('|')})(?!\\w)`, 'gi');
}

const patternToSlug = new Map<string, { slug: string; label: string }>();

function ensurePatternMap(): void {
  if (patternToSlug.size > 0) return;
  for (const entry of getPowerMentionIndex()) {
    const key = entry.pattern.toLowerCase();
    if (!patternToSlug.has(key)) {
      patternToSlug.set(key, { slug: entry.slug, label: entry.label });
    }
  }
}

export function resolveMentionMatch(matchedText: string): { slug: string; label: string } | null {
  ensurePatternMap();
  return patternToSlug.get(matchedText.toLowerCase()) ?? null;
}

/** Normaliza o conteúdo interno de um bbcode [[...]] para um slug de power. */
export function normalizePowerSlug(inner: string): string {
  return inner
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '');
}

/**
 * Resolve o conteúdo de um bbcode `[[nome ou slug]]` para uma menção.
 * O rótulo visível respeita a capitalização escrita no bbcode.
 */
export function resolveBracketMention(
  inner: string
): { slug: string; label: string } | null {
  const trimmed = inner.trim();
  if (!trimmed) return null;

  const known = resolveMentionMatch(trimmed);
  if (known) return { slug: known.slug, label: trimmed };

  const slug = normalizePowerSlug(trimmed);
  if (!slug) return null;
  return { slug, label: trimmed };
}
